import type { DashboardLayout, DashboardWidget, WidgetSize } from './widget-types.ts';

export const MAX_WIDGETS = 24;

export type Shift = 'earlier' | 'later';

const SHIFT_STEPS: Readonly<Record<Shift, number>> = { earlier: -1, later: 1 };

const withWidgets = (layout: DashboardLayout, widgets: readonly DashboardWidget[]): DashboardLayout => ({
  ...layout,
  widgets,
});

export const hasRoomForWidget = (layout: DashboardLayout): boolean => layout.widgets.length < MAX_WIDGETS;

export const freshWidgetId = (layout: DashboardLayout, kind: string): string => {
  const taken = new Set(layout.widgets.map((widget) => widget.id));
  const free = Array.from({ length: MAX_WIDGETS + 1 }, (_, index) => `${kind}-${String(index + 1)}`).find(
    (id) => !taken.has(id),
  );
  return free ?? `${kind}-${String(taken.size + 1)}`;
};

export const addedWidget = (layout: DashboardLayout, widget: DashboardWidget): DashboardLayout =>
  hasRoomForWidget(layout) ? withWidgets(layout, [...layout.widgets, widget]) : layout;

export const removedWidget = (layout: DashboardLayout, id: string): DashboardLayout =>
  withWidgets(
    layout,
    layout.widgets.filter((widget) => widget.id !== id),
  );

export const replacedWidget = (layout: DashboardLayout, changed: DashboardWidget): DashboardLayout =>
  withWidgets(
    layout,
    layout.widgets.map((widget) => (widget.id === changed.id ? changed : widget)),
  );

export const resizedWidget = (layout: DashboardLayout, id: string, size: WidgetSize): DashboardLayout =>
  withWidgets(
    layout,
    layout.widgets.map((widget) => (widget.id === id ? { ...widget, size } : widget)),
  );

export const movedWidgetTo = (layout: DashboardLayout, id: string, target: number): DashboardLayout => {
  const moving = layout.widgets.find((widget) => widget.id === id);
  if (moving === undefined) return layout;
  const others = layout.widgets.filter((widget) => widget.id !== id);
  const at = Math.min(Math.max(target, 0), others.length);
  return withWidgets(layout, [...others.slice(0, at), moving, ...others.slice(at)]);
};

export const shiftedWidget = (layout: DashboardLayout, id: string, shift: Shift): DashboardLayout =>
  movedWidgetTo(layout, id, layout.widgets.findIndex((widget) => widget.id === id) + SHIFT_STEPS[shift]);
